<template>
  <div class="home">
    <LoaderComponent v-show="loading" />
    <HeaderView />

    <section
      class="order-page-main-sec order-invoice-page-main-sec agency-design-grid-right"
    >
      <div class="desktop-respovsive-main-sec">
        <div class="editor-dashboard-sec-2" id="printSection">
          <div class="order-list-tabs">
            <div class="invoice-logo">
              <a href="#"><img src="@/assets/common/images/lbhq-logo.svg" alt="Link Building HQ" /></a>
            </div>
            <div class="invoice-detal-list invoice-view-billto-only">
              <ul>
                <li><b>Bill To</b></li>
                <li><b>{{ agencyName || "—" }}</b></li>
                <li v-if="billingEmailDisplay" class="invoice-email-text">{{ billingEmailDisplay }}</li>
                <li v-if="issuedAt"><b>Invoice Date:</b> {{ issuedAt }}</li>
                <li><b>Invoice # {{ invoiceNo || "—" }}</b></li>
              </ul>
            </div>
          </div>

          <div class="table-responsive custom-order-class">
            <form>
              <table class="addon-table billing-invoice-table">
                <tr>
                  <th class="col-custom-id">Order ID</th>
                  <th class="col-custom-title">Order Title</th>
                  <th class="col-custom-links">Total Links</th>
                  <th class="col-custom-amount">Amount</th>
                </tr>
                <tr
                  v-for="(row, index) in invoiceRows"
                  :key="'inv-row-' + index"
                >
                  <td class="col-custom-id">{{ row.id }}</td>
                  <td class="col-custom-title">{{ row.order_title }}</td>
                  <td class="col-custom-links">{{ row.total_links }}</td>
                  <td class="col-custom-amount">
                    {{ numberFormat(row.price, "currency") }}
                  </td>
                </tr>
              </table>
            </form>
          </div>

          <div class="proceed-box">
            <div class="total-price-table-box proceed-pay-box custom-invoice-summary">
              <table style="width: 100%">
                <tr class="bg-text">
                  <td colspan="2"></td>
                  <td>Qty</td>
                  <td>Price</td>
                </tr>
                <tr
                  v-for="(summaryRow, index) in summaryRows"
                  :key="'summary-' + index"
                >
                  <td colspan="2">{{ summaryRow.label }}</td>
                  <td>{{ summaryRow.qty }}</td>
                  <td>{{ summaryRow.amount }}</td>
                </tr>
                <tr>
                  <td colspan="2">Subtotal</td>
                  <td colspan="2">USD {{ numberFormat(subTotal, "currency") }}</td>
                </tr>
                <tr v-if="discount > 0">
                  <td colspan="2">Discount (1%)</td>
                  <td colspan="2">(USD {{ numberFormat(discount, "currency") }})</td>
                </tr>
                <tr>
                  <td colspan="2">Total</td>
                  <td colspan="2">
                    <b>USD {{ numberFormat(orderTotal, "currency") }}</b>
                  </td>
                </tr>
              </table>
            </div>
          </div>

          <div class="btn-box-list">
            <a href="javascript:;" @click="downloadInvoice">
              <i class="fas fa-download"></i> Download
            </a>
            <a
              v-if="showPayButton"
              href="javascript:;"
              class="pay-invoice-btn"
              @click="payInvoice"
            >
              <i class="fas fa-credit-card"></i> Pay
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
const toast = useToast();
import HeaderView from "@/components/layout/HeaderComponent.vue";
import LoaderComponent from "@/components/LoaderComponent.vue";
import { formatEmailDisplay, numberFormat } from "@/prod.js";

export default {
  name: "InvoiceViewPage",
  components: {
    HeaderView,
    LoaderComponent,
  },
  data() {
    return {
      numberFormat,
      loading: false,
      invoiceId: "",
      invoiceNo: "",
      agencyName: "",
      billingEmail: "",
      issuedAt: "",
      invoiceRows: [],
      summaryRows: [],
      orderTotal: 0,
      subTotal: 0,
      discount: 0,
      status: "",
      paymentToken: "",
    };
  },
  computed: {
    billingEmailDisplay() {
      return formatEmailDisplay(this.billingEmail);
    },
    isInvoicePaid() {
      return String(this.status || "").toLowerCase() === "paid";
    },
    showPayButton() {
      const status = String(this.status || "open").toLowerCase();
      return status === "open" || status === "";
    },
  },
  mounted() {
    this.loadInvoice();
  },
  methods: {
    async loadInvoice() {
      const id = this.$route.params.id;
      if (!id) {
        this.$router.push("/invoices");
        return;
      }
      this.invoiceId = id;

      this.loading = true;
      try {
        const res = await this.$store.dispatch(
          "Billing/getOpenInvoiceViewAction",
          id
        );
        if (!res?.success) {
          this.$router.push("/invoices");
          return;
        }

        this.invoiceNo = res.invoice_no || "";
        this.agencyName = res.agency_name || "";
        this.billingEmail = formatEmailDisplay(res.billing_email);
        this.issuedAt = res.issued_at || "";
        this.invoiceRows = Array.isArray(res.invoice_detail) ? res.invoice_detail : [];
        this.summaryRows = Array.isArray(res.summary_rows) ? res.summary_rows : [];
        this.orderTotal = Number(res.order_total) || 0;
        this.subTotal = Number(res.sub_total) || this.orderTotal;
        this.discount = Number(res.discount) || 0;
        this.status = res.status || "";
        this.paymentToken = await this.resolvePaymentToken(res);
      } catch {
        toast.error("Unable to load invoice.", { timeout: 3000 });
        this.$router.push("/invoices");
      } finally {
        this.loading = false;
      }
    },
    async resolvePaymentToken(viewResponse) {
      let token =
        viewResponse?.payment_token || this.$route.query.data || "";

      if (token || this.isInvoicePaid) {
        return token;
      }

      try {
        const listResponse = await this.$store.dispatch(
          "Billing/getOpenInvoicesAction",
          {
            s: "",
            e: "",
            order_date: "",
            per_page: 500,
            page: 1,
          }
        );
        const invoice = (listResponse.data || []).find(
          (item) => String(item.id) === String(this.invoiceId)
        );
        token = invoice?.payment_token || "";
      } catch {
        token = "";
      }

      return token;
    },
    payInvoice() {
      if (!this.paymentToken) {
        toast.error("Payment link is unavailable.", { timeout: 3000 });
        return;
      }
      const routeData = this.$router.resolve({
        path: "/invoice-payment",
        query: { data: this.paymentToken },
      });
      window.open(routeData.href, "_blank");
    },
    async downloadInvoice() {
      if (!this.invoiceId) {
        return;
      }

      this.loading = true;
      try {
        const response = await this.$store.dispatch(
          "Billing/downloadOpenInvoiceAction",
          this.invoiceId
        );
        if (response && response.type && response.type.indexOf("application/json") !== -1) {
          toast.error("Invoice PDF could not be downloaded.", { timeout: 3000 });
          return;
        }
        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = (this.invoiceNo || ("INV-" + this.invoiceId)) + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch {
        toast.error("Invoice PDF could not be downloaded.", { timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-view-billto-only {
  justify-content: flex-end;
  flex-shrink: 0;
}

.invoice-view-billto-only :deep(ul) {
  width: auto;
  min-width: 380px;
  max-width: 460px;
  text-align: left;
}

.invoice-view-billto-only :deep(ul li) {
  white-space: nowrap;
  word-break: normal;
}

.invoice-view-billto-only :deep(ul li.invoice-email-text) {
  text-transform: lowercase;
}

.btn-box-list a.pay-invoice-btn {
  background-color: #00b6cb;
  border-color: #00b6cb;
  color: #fff;
}

:deep(.btn-box-list) a.pay-invoice-btn:hover {
  color: #fff;
}
</style>
